import type { Editor } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import type { Instance as TippyInstance } from "tippy.js";
import tippy from "tippy.js";
import "tippy.js/themes/translucent.css";
import { trpcClient } from "@/lib/trpc";
import MentionList from "./MentionList";
import { UserMentionComponent } from "./UserMentionComponent";

interface UserMentionListRef {
    onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}
const userSuggestion = {
    pluginKey: new PluginKey("suggestionTwo"),
    items: async ({ query }: { query: string }) => {
        const members = await trpcClient.user.members.query({ skipCurrentUser: true });
        return members.filter((member) => member.name.toLowerCase().startsWith(query.toLowerCase())).slice(0, 10);
    },
    render: () => {
        let component: ReactRenderer<UserMentionListRef>;
        let popup: TippyInstance;

        return {
            onStart: (props: SuggestionProps) => {
                component = new ReactRenderer(MentionList, {
                    props,
                    editor: props.editor as Editor,
                });

                if (!props.clientRect) {
                    return;
                }

                popup = tippy(document.body, {
                    getReferenceClientRect: props.clientRect as () => DOMRect,
                    appendTo: () => document.body,
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: "manual",
                    theme: "usersuggestion",
                    placement: "bottom-start",
                });
            },

            onUpdate(props: SuggestionProps) {
                component.updateProps(props);

                if (!props.clientRect) {
                    return;
                }

                popup.setProps({
                    getReferenceClientRect: props.clientRect as () => DOMRect,
                });
            },

            onKeyDown(props: SuggestionKeyDownProps) {
                if (props.event.key === "Escape") {
                    popup.hide();

                    return true;
                }

                return component.ref?.onKeyDown(props) ?? false;
            },

            onExit() {
                popup.destroy();
                component.destroy();
            },
        };
    },
};

export const mentionListExtension = () => {
    return {
        name: "userMention",
        addAttributes() {
            return {
                id: {
                    default: null,
                    parseHTML: (element: HTMLElement) => element.getAttribute("id"),
                    renderHTML: (attributes: Record<string, any>) => {
                        if (!attributes.id) {
                            return {};
                        }
                        return { id: attributes.id };
                    },
                },
                label: {
                    default: null,
                    parseHTML: (element: HTMLElement) => element.getAttribute("data-label"),
                    renderHTML: (attributes: Record<string, any>) => {
                        if (!attributes.label) {
                            return {};
                        }
                        return { "data-label": attributes.label };
                    },
                },
                email: {
                    default: null,
                    parseHTML: (element: HTMLElement) => element.getAttribute("data-email"),
                    renderHTML: (attributes: Record<string, any>) => {
                        if (!attributes.email) {
                            return {};
                        }
                        return { "data-email": attributes.email };
                    },
                },
                idempotencyKey: {
                    default: null,
                    parseHTML: (element: HTMLElement) => element.getAttribute("data-idempotency-key"),
                    renderHTML: (attributes: Record<string, any>) => {
                        if (!attributes.idempotencyKey) {
                            return {};
                        }
                        return { "data-idempotency-key": attributes.idempotencyKey };
                    },
                },
            };
        },
        addNodeView() {
            return ReactNodeViewRenderer(UserMentionComponent);
        },
    };
};

export const mentionListConfiguration = () => {
    return {
        HTMLAttributes: {
            class: "mention mention--user",
        },
        deleteTriggerWithBackspace: true,
        suggestion: userSuggestion,
        renderText({ node }: { node: any }) {
            return `${node.attrs.label ?? node.attrs.id}`;
        },
    };
};
